import { useMemo, useState, type FormEvent } from 'react';
import { Client, type CaseDetail } from './api';

type Props = { detail: CaseDetail; client: Client; notify: (message: string) => void; refresh: () => Promise<void> };
type Kind = 'geospatial' | 'communication' | 'vehicle' | 'forensic' | 'multimodal' | 'scenario';
const text = (value: unknown) => String(value ?? '');

export default function SpecializedEvidencePanel({ detail, client, notify, refresh }: Props) {
  const usableEvidence = useMemo(() => detail.evidence.filter((row) => row.integrity_status === 'verified' && row.cutoff_status === 'accepted'), [detail.evidence]);
  const [kind, setKind] = useState<Kind>('geospatial');
  const [evidenceId, setEvidenceId] = useState('');
  const [title, setTitle] = useState('');
  const [secondary, setSecondary] = useState('');
  const [when, setWhen] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [notes, setNotes] = useState('');
  const [uncertainty, setUncertainty] = useState('Limitação e incerteza devem ser confirmadas por especialista humano.');
  const [busy, setBusy] = useState(false);

  const reset = () => { setTitle(''); setSecondary(''); setWhen(''); setLatitude(''); setLongitude(''); setNotes(''); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!evidenceId) { notify('Selecione uma evidência íntegra e anterior ao corte.'); return; }
    setBusy(true);
    try {
      const occurredAt = when ? new Date(when).toISOString() : undefined;
      if (kind === 'geospatial') {
        await client.createGeospatialObservation(detail.case.id, {
          evidenceId, label: title, ...(occurredAt ? { eventAt: occurredAt } : {}),
          ...(latitude && longitude ? { latitude: Number(latitude), longitude: Number(longitude) } : {}),
          locationText: secondary || undefined, observation: notes || undefined, coordinateMethod: 'document_declared'
        });
      } else if (kind === 'communication') {
        await client.createCommunicationRecord(detail.case.id, {
          evidenceId, medium: secondary || 'registro_documental', direction: 'unknown',
          ...(occurredAt ? { occurredAt } : {}), summary: notes || title
        });
      } else if (kind === 'vehicle') {
        await client.createVehicleEvent(detail.case.id, {
          evidenceId, vehicleLabel: title, eventType: secondary || 'evento_documentado',
          ...(occurredAt ? { eventAt: occurredAt } : {}),
          ...(latitude && longitude ? { latitude: Number(latitude), longitude: Number(longitude) } : {}), observation: notes || undefined
        });
      } else if (kind === 'forensic') {
        await client.createForensicObservation(detail.case.id, {
          evidenceId, domain: secondary || 'physical', observation: notes || title,
          method: title || undefined, uncertaintyText: uncertainty
        });
      } else if (kind === 'multimodal') {
        await client.createMultimodalObservation(detail.case.id, {
          evidenceId, mediaType: (secondary || 'document'), observationType: title || 'observacao',
          observation: notes || title, technique: 'manual_or_authorized_model_observation'
        });
      } else {
        await client.createScenario(detail.case.id, {
          scenarioName: title,
          assumptions: notes.split('\n').map((item) => item.trim()).filter(Boolean),
          missingInformation: secondary ? secondary.split('\n').map((item) => item.trim()).filter(Boolean) : [],
          supportingEvidenceIds: [evidenceId], contradictingEvidenceIds: [],
          limitationText: uncertainty
        });
      }
      await refresh(); reset(); notify('Registro especializado criado e vinculado à evidência.');
    } catch (error) { notify(error instanceof Error ? error.message : 'Falha ao criar registro especializado'); }
    finally { setBusy(false); }
  };

  const labels: Record<Kind, { title: string; secondary: string; notes: string }> = {
    geospatial: { title: 'Rótulo do ponto/evento', secondary: 'Local descrito na fonte', notes: 'Observação' },
    communication: { title: 'Título/descrição curta', secondary: 'Meio (telefone, mensagem, registro etc.)', notes: 'Resumo do registro' },
    vehicle: { title: 'Veículo/rótulo pseudonimizado', secondary: 'Tipo de evento', notes: 'Observação logística' },
    forensic: { title: 'Método ou referência do exame', secondary: 'Domínio (physical, medical, digital...)', notes: 'Observação pericial extraída' },
    multimodal: { title: 'Tipo de observação', secondary: 'Mídia (document, image, audio, video)', notes: 'Observação sem identificação biométrica' },
    scenario: { title: 'Nome do cenário exploratório', secondary: 'Informações ausentes, uma por linha', notes: 'Premissas, uma por linha' }
  };
  const field = labels[kind];

  return <div className="cc-two-col specialized-panel">
    <section className="cc-card"><h3>Registrar dado especializado</h3>
      <p>Todo registro é derivado de uma evidência já ingerida, com hash verificado e anterior à data de corte.</p>
      <form className="cc-form" onSubmit={submit}>
        <label>Modalidade<select value={kind} onChange={(e)=>setKind(e.target.value as Kind)}>
          <option value="geospatial">Geoespacial</option><option value="communication">Comunicação</option><option value="vehicle">Veículo/logística</option><option value="forensic">Perícia existente</option><option value="multimodal">Multimodal</option><option value="scenario">Cenário exploratório</option>
        </select></label>
        <label>Evidência de origem<select value={evidenceId} onChange={(e)=>setEvidenceId(e.target.value)} required><option value="">Selecione…</option>{usableEvidence.map((item)=><option key={text(item.id)} value={text(item.id)}>{text(item.title)} · {text(item.evidence_type)}</option>)}</select></label>
        <label>{field.title}<input value={title} onChange={(e)=>setTitle(e.target.value)} required minLength={2}/></label>
        <label>{field.secondary}<textarea value={secondary} onChange={(e)=>setSecondary(e.target.value)} rows={2}/></label>
        {kind !== 'forensic' && kind !== 'multimodal' && kind !== 'scenario' && <label>Data/hora da fonte<input type="datetime-local" value={when} onChange={(e)=>setWhen(e.target.value)}/></label>}
        {(kind === 'geospatial' || kind === 'vehicle') && <div className="form-grid"><label>Latitude<input type="number" step="any" min="-90" max="90" value={latitude} onChange={(e)=>setLatitude(e.target.value)}/></label><label>Longitude<input type="number" step="any" min="-180" max="180" value={longitude} onChange={(e)=>setLongitude(e.target.value)}/></label></div>}
        <label>{field.notes}<textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={5} required={kind==='scenario'}/></label>
        {(kind === 'forensic' || kind === 'scenario') && <label>Limitação/incerteza<textarea value={uncertainty} onChange={(e)=>setUncertainty(e.target.value)} rows={3} minLength={10} required/></label>}
        <button className="primary" disabled={busy || usableEvidence.length===0}>{busy ? 'Salvando…' : 'Registrar com rastreabilidade'}</button>
      </form>
    </section>
    <section className="cc-card"><h3>Limites por desenho</h3><ul className="cc-bullets">
      <li>Geoespacial usa somente coordenadas documentadas; não rastreia pessoa em tempo real.</li>
      <li>Comunicações usam registros fornecidos legalmente ao caso; não acessam contas privadas.</li>
      <li>Perícia registra observações de laudos existentes; não certifica autenticidade ou causa.</li>
      <li>Multimodal não realiza identificação facial ou vocal.</li>
      <li>Simulação de cenário mede cobertura e equilíbrio da evidência; não estima culpa ou autoria.</li>
      <li>Todos os registros permanecem pendentes de revisão humana.</li>
    </ul></section>
  </div>;
}
